<template>
  <div
    ref="splitterContainer"
    class="splitter-container"
    :style="{
      display: 'flex',
      flexDirection: direction === 'horizontal' ? 'row' : 'column',
      height: '100%',
      width: '100%',
      overflow: 'hidden',
    }"
  >
    <div
      ref="firstPanel"
      class="splitter-panel first-panel"
      :class="{ collapsed: isCollapsed }"
      :style="{
        flex: isCollapsed ? '0 0 0px' : `0 0 ${firstPanelSize}px`,
        overflow: 'auto',
        position: 'relative',
        transition: isResizing ? 'none' : 'flex-basis 0.1s ease-out',
      }"
    >
      <slot name="first"></slot>
    </div>

    <!-- Enhanced Transition for Second Panel -->
    <Transition name="sidepane-slide">
      <div
        v-if="props.showSecondPanel"
        ref="secondPanel"
        class="splitter-panel second-panel border-l border-gray-300"
        :style="{
          flex: '1 1 auto',
          overflow: 'auto',
          position: 'relative',
          marginLeft: '20px',
          transition: isResizing ? 'none' : 'flex 0.1s ease-out',
        }"
      >
        <slot name="second"></slot>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from "vue";

interface Props {
  direction?: "horizontal" | "vertical";
  initialSplit?: number;
  minSize?: number;
  maxSize?: number;
  localStorageKey?: string;
  isCollapsed?: boolean;
  showSecondPanel?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  direction: "horizontal",
  initialSplit: 40,
  minSize: 40,
  maxSize: 65,
  localStorageKey: "splitter-panel-size",
  isCollapsed: false,
  showSecondPanel: true,
});

const emit = defineEmits<{
  (e: "resize", sizes: { first: number; second: number }): void;
  (e: "resizeStart"): void;
  (e: "resizeEnd"): void;
  (e: "update:isCollapsed", value: boolean): void;
}>();

const splitterContainer = ref<HTMLElement | null>(null);
const firstPanel = ref<HTMLElement | null>(null);
const secondPanel = ref<HTMLElement | null>(null);
const splitterDivider = ref<HTMLElement | null>(null);
const isResizing = ref(false);
const startPosition = ref(0);
const firstPanelSize = ref(0);
const containerSize = ref(0);
const isCollapsed = ref(props.isCollapsed);
const previousSize = ref(0);

// Load saved size from localStorage if available
onMounted(() => {
  const savedSize = localStorage.getItem(props.localStorageKey);

  if (splitterContainer.value) {
    const containerSize =
      props.direction === "horizontal"
        ? splitterContainer.value.clientWidth
        : splitterContainer.value.clientHeight;

    if (savedSize) {
      // Convert saved percentage to pixels
      firstPanelSize.value = (parseInt(savedSize) / 100) * containerSize;
    } else {
      // Use initial split percentage
      firstPanelSize.value = (props.initialSplit / 100) * containerSize;
    }
  }

  window.addEventListener("resize", handleWindowResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleWindowResize);
  document.removeEventListener("mousemove", handleMouseMove);
  document.removeEventListener("mouseup", handleMouseUp);
  document.removeEventListener("touchmove", handleTouchMove);
  document.removeEventListener("touchend", handleTouchEnd);
});

const handleWindowResize = () => {
  if (splitterContainer.value) {
    containerSize.value =
      props.direction === "horizontal"
        ? splitterContainer.value.clientWidth
        : splitterContainer.value.clientHeight;

    // Ensure first panel size is still valid
    validateSize();
  }
};

const startResize = (event: MouseEvent | TouchEvent) => {
  isResizing.value = true;

  if (event instanceof MouseEvent) {
    startPosition.value =
      props.direction === "horizontal" ? event.clientX : event.clientY;
  } else {
    const touch = event.touches[0];
    startPosition.value =
      props.direction === "horizontal" ? touch.clientX : touch.clientY;
  }

  // Store the current size
  if (firstPanel.value) {
    firstPanelSize.value =
      props.direction === "horizontal"
        ? firstPanel.value.offsetWidth
        : firstPanel.value.offsetHeight;
  }

  // Add event listeners
  document.addEventListener("mousemove", handleMouseMove);
  document.addEventListener("mouseup", handleMouseUp);
  document.addEventListener("touchmove", handleTouchMove, { passive: false });
  document.addEventListener("touchend", handleTouchEnd);

  // Add cursor styles to body
  document.body.style.cursor =
    props.direction === "horizontal" ? "col-resize" : "row-resize";
  document.body.style.userSelect = "none";

  emit("resizeStart");
};

const handleMouseMove = (event: MouseEvent) => {
  if (!isResizing.value) return;

  // Use requestAnimationFrame for smoother updates
  requestAnimationFrame(() => {
    updatePanelSize(event);
    event.preventDefault();
  });
};

const handleTouchMove = (event: TouchEvent) => {
  if (!isResizing.value) return;

  const touch = event.touches[0];
  // Use requestAnimationFrame for smoother updates
  requestAnimationFrame(() => {
    // Create a synthetic mouse event for touch
    const syntheticEvent = {
      clientX: touch.clientX,
      clientY: touch.clientY,
    } as MouseEvent;
    updatePanelSize(syntheticEvent);
    event.preventDefault();
  });
};

const updatePanelSize = (event: MouseEvent) => {
  if (!splitterContainer.value) return;

  const containerSize =
    props.direction === "horizontal"
      ? splitterContainer.value.clientWidth
      : splitterContainer.value.clientHeight;

  // Get the divider width to account for it in calculations
  const dividerWidth = splitterDivider.value?.offsetWidth || 0;

  // Calculate new size directly from mouse position
  const mousePosition =
    props.direction === "horizontal" ? event.clientX : event.clientY;

  const containerRect = splitterContainer.value.getBoundingClientRect();
  const containerStart =
    props.direction === "horizontal" ? containerRect.left : containerRect.top;

  // Calculate position relative to container
  const relativePosition = mousePosition - containerStart;

  // Calculate percentage directly from mouse position
  const newSizePercent = (relativePosition / containerSize) * 100;

  // Constrain to min and max percentages
  const constrainedPercent = Math.max(
    props.minSize,
    Math.min(newSizePercent, props.maxSize),
  );

  // Convert to pixels
  firstPanelSize.value = (constrainedPercent / 100) * containerSize;

  // Calculate second panel size
  const secondPanelSize = containerSize - firstPanelSize.value - dividerWidth;

  // Emit resize event with percentages
  emit("resize", {
    first: constrainedPercent,
    second: (secondPanelSize / containerSize) * 100,
  });
};

const validateSize = () => {
  if (!splitterContainer.value) return;

  const containerSize =
    props.direction === "horizontal"
      ? splitterContainer.value.clientWidth
      : splitterContainer.value.clientHeight;

  const currentPercent = (firstPanelSize.value / containerSize) * 100;

  // Ensure percentage is within bounds
  if (currentPercent < props.minSize) {
    firstPanelSize.value = (props.minSize / 100) * containerSize;
  } else if (currentPercent > props.maxSize) {
    firstPanelSize.value = (props.maxSize / 100) * containerSize;
  }
};

const handleMouseUp = () => {
  endResize();
};

const handleTouchEnd = () => {
  endResize();
};

const endResize = () => {
  if (!isResizing.value || !splitterContainer.value) return;

  isResizing.value = false;

  // Remove event listeners
  document.removeEventListener("mousemove", handleMouseMove);
  document.removeEventListener("mouseup", handleMouseUp);
  document.removeEventListener("touchmove", handleTouchMove);
  document.removeEventListener("touchend", handleTouchEnd);

  // Reset cursor
  document.body.style.cursor = "";
  document.body.style.userSelect = "";

  // Save percentage to localStorage
  const containerSize =
    props.direction === "horizontal"
      ? splitterContainer.value.clientWidth
      : splitterContainer.value.clientHeight;
  const percentage = (firstPanelSize.value / containerSize) * 100;
  localStorage.setItem(props.localStorageKey, percentage.toString());

  emit("resizeEnd");
};

// Add after onMounted
watch(
  () => props.isCollapsed,
  (newValue) => {
    isCollapsed.value = newValue;
    if (newValue) {
      // Store current size before collapsing
      previousSize.value = firstPanelSize.value;
      firstPanelSize.value = 0;
    } else {
      // Restore previous size
      firstPanelSize.value =
        previousSize.value ||
        (props.initialSplit / 100) *
          (splitterContainer.value?.clientWidth || 0);
    }
  },
);

const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value;
  emit("update:isCollapsed", isCollapsed.value);
};
</script>

<style scoped>
.splitter-container {
  position: relative;
  user-select: none;
}

.splitter-divider {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 10;
  transition:
    background-color 0.2s,
    box-shadow 0.2s;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.04);
  border-radius: 6px;
}

.splitter-divider.horizontal {
  width: 24px;
  min-width: 24px;
  margin: 20px -14px;
  height: 100%;
}

.splitter-divider.vertical {
  cursor: row-resize;
  height: 24px;
  min-height: 24px;
  margin: -12px 0;
  width: 100%;
}

.splitter-divider-icon {
  cursor: col-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: 37px;
  position: relative;
  color: gray;
}

.splitter-divider:hover,
.splitter-divider.collapsed {
  opacity: 1;
}

.splitter-panel {
  min-width: 0;
  min-height: 0;
  will-change: flex-basis;
}

.splitter-panel.first-panel {
  will-change: flex-basis;
  scrollbar-width: none;
}

.splitter-panel.second-panel {
  will-change: flex;
}

.splitter-panel.collapsed {
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.splitter-divider.collapsed {
  background-color: #f0f0f0;
}

/* Enhanced slide-in animation - keeping your original structure but improving smoothness */
.sidepane-slide-enter-from,
.sidepane-slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.sidepane-slide-enter-active {
  transition:
    transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1),
    opacity 0.4s ease;
}

.sidepane-slide-leave-active {
  transition:
    transform 0.3s cubic-bezier(0.4, 0, 0.6, 1),
    opacity 0.3s ease;
}

.sidepane-slide-enter-to,
.sidepane-slide-leave-from {
  transform: translateX(0%);
  opacity: 1;
}
</style>
